import getTooltipFormatter from 'src/components/Charts/tooltipResolver';
import legendTooltipFormatter from './legendTooltipFormatter';
import { BAR } from 'src/components/Charts/types';
import { toEchartData } from '../dataProcessor';
import { getLabelFormatterThatDisplaysOnly } from './labelFormatter';
import { capitalizeText } from 'src/util/formatters/stringNormalizer';

export default function toEchartOption(resultadosGerais, theme, labelFormatter, chartFontSize, disabledLegends, highlitIndex, ascOrder = false) {
  labelFormatter = labelFormatter ?? getLabelFormatterThatDisplaysOnly(0, false);
  disabledLegends = disabledLegends ?? [];
  const echartData = toEchartData(resultadosGerais);
  const { data, legends } = echartData;
  // Considera o caso em que existem consolidações
  const formatter = getTooltipFormatter(resultadosGerais, BAR);

  /**
   * mapeando cada legenda para uma entrada que possui
   * o nome da legenda e o ícone círculo.
   */
  const legendData = legends.map((legend) => ({
    name: legend,
    icon: 'circle',
    enabled: true,
  }));

  // inverte a ordem das legendas para seguir a ordem das barras do gráfico
  const newLegendData = ascOrder ? [...legendData].reverse() : [...legendData];

  const legendSelected = {};
  newLegendData.forEach((legend) => {
    const enabled = !disabledLegends.includes(legend.name);
    legendSelected[legend.name] = enabled;
  });

  /**
   * criando um conjunto de series.
   *
   * Cada serie criada representará uma barra no gráfico.
   * Esta abordagem é necessária, pois no echarts, cada legenda remete a uma série.
   */
  const series = data.map((item, index) => {
    const dataEntry = new Array(data.length);
    const dataEntryIndex = (data.length - 1) - index;
    const revert = ascOrder ? index : dataEntryIndex
    dataEntry[revert] = item;

    return {
      name: item.name,
      type: 'bar',
      data: dataEntry,
      stack: 'stack',
      label: {
        formatter: labelFormatter,
        textStyle: {
          fontSize: chartFontSize,
          color: theme.label.text,
        },
      },

      ...(highlitIndex === undefined ? {} : {
        itemStyle: index === highlitIndex ? {
          borderType: theme.highlitedItemBorderType,
          borderColor: theme.highlitedItemBorderColor,
          borderWidth: theme.highlitedItemBorderWidth,
        } : null,
      }),
    };
  });

  // Realoca o array de series acordo com as legedendas desabilitadas / habilitadas
  // esse algorítmo faz com que a realocação das "barras" ocorra.
  let auxIndex = 0;
  // Foi feito um spread operator em series porque não queremos dar um reverse na referência real do array.
  const newSeries = ascOrder ? [...series] : [...series].reverse();

  newSeries.forEach((item) => {
    const itemDataHidden = !legendSelected[item.name];
    auxIndex += Number(itemDataHidden);

    const findedItemIndex = item.data.findIndex((itemData) => !!itemData);
    const indexReference = !itemDataHidden ? findedItemIndex - auxIndex : item.data.length - auxIndex;

    if (findedItemIndex >= 0) {
      const findedItemAux = item.data[findedItemIndex];
      // eslint-disable-next-line no-param-reassign
      item.data[findedItemIndex] = null;
      // eslint-disable-next-line no-param-reassign
      item.data[indexReference] = findedItemAux;
    }
  });

  return {
    color: theme.colors,
    visibleLegends: newLegendData,
    animationDuration: 0,
    grid: {
      left: '2%',
      right: '7%',
      bottom: '3%',
      containLabel: true,
    },
    label: {
      show: true,
      position: 'top',
    },
    tooltip: {
      show: true,
      trigger: 'item',
      formatter,
    },
    legend: {
      data: newLegendData,
      selected: legendSelected,
      type: 'scroll',
      tooltip: {
        show: true,
        trigger: 'item',
        formatter: legendTooltipFormatter(newSeries, disabledLegends, formatter),
      },
      textStyle: {
        fontSize: chartFontSize,
        color: theme.legend.color,
        fontFamily: 'Roboto, sans-serif',
      },
      formatter: capitalizeText,
      pageTextStyle: {
        color: theme.legend.pagination.text.color,
      },
      pageIconColor: theme.legend.pagination.buttons.color,
    },
    xAxis: {
      type: 'category',
      // O filtro foi adicionado para que os itens do gráfico fossem
      // redimencionados no eixo x ao habilitar/desabilitar uma legenda.
      data: legends.filter((legend) => legendSelected[legend]),
      // no modo vertical (padrão) os dados da linha y
      // não são exibidos
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        textStyle: {
          color: theme.axis.x.label,
        },
        formatter: (value) => `${value}%`,
      },
      axisLine: {
        lineStyle: {
          color: theme.axis.x.line,
        },
      },
      boundaryGap: true,
    },
    series: newSeries,
  };
}
